import React, { useEffect, useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getHealingTrajectoryAsync } from "../../../app/patientSlice";
import { format } from "date-fns";
import Slider from "rc-slider";
import "rc-slider/assets/index.css";
import "./healingTrajectory.css";
import styles from "./healingTrajectory.module.scss";
import { BWAT } from "../../../constants";
import { allNullCheck } from "../../../utils";
import { useParams } from "react-router-dom";
import HealingTrajectoryChart from "../../secondary/HealingTrajectoryChart/HealingTrajectoryChart";
import { useLoading } from "../../../hooks/useLoading";
import { LOADING_KEYS } from "../../../constants/loadingKeys";
import { Spinner } from "../../secondary/Loader/Spinner";

export function HealingTrajectory({ detailAssessment }) {
  const { assessmentId, patientId } = useParams();
  const dispatch = useDispatch();

  const woundProgressOptions = useSelector((state) => state.patient.woundProgress);
  const fetchedHealingData = useSelector((state) => state.patient.healingTrajectoryData);
  const userDetailsAlt = useSelector((state) => state.user.userDetails);
  const isLoading = useLoading(LOADING_KEYS.HEALING_TRAJECTORY);

  const [currentAssess, setcurrentAssess] = useState(0);
  const [surfaceArray, setSurfaceArray] = useState([]);
  const [currPoint, setCurrPoint] = useState([]);
  const [scoreType, setScoreType] = useState("");

  const healTraj = useMemo(() => {
    return fetchedHealingData?.filter((data) => data?.dimensions?.surfaceArea?.value) || [];
  }, [fetchedHealingData]);

  const max = healTraj.length ? healTraj.length - 1 : 0;

  useEffect(() => {
    const payload = {
      patientId,
      assessmentId,
    };
    dispatch(getHealingTrajectoryAsync(payload));
  }, [assessmentId, dispatch, patientId]);

  useEffect(() => {
    if (healTraj.length) {
      setcurrentAssess(max);
    }
  }, [healTraj, max]);

  const handleSlider = (e) => {
    setcurrentAssess(e);
  };

  useEffect(() => {
    if (!healTraj?.length) {
      setSurfaceArray([]);
      return;
    }

    const surfaceData = healTraj
      .filter((data) => !Number.isNaN(Number(data?.dimensions?.surfaceArea?.value)))
      .map((data) => {
        const dateObj = new Date(data?.measurementDate);

        return {
          x: `${format(dateObj, "LL/dd")} ${format(dateObj, "h:mm a")}`,
          y: data?.dimensions?.surfaceArea?.value,
          date: format(dateObj, "LL/dd"),
        };
      });

    setSurfaceArray(surfaceData);
  }, [healTraj]);

  useEffect(() => {
    if (!healTraj?.length || healTraj.length <= 1) {
      setCurrPoint([]);
      return;
    }

    const assess = healTraj[currentAssess];
    const value = assess?.dimensions?.surfaceArea?.value;

    if (Number.isNaN(Number(value))) {
      setCurrPoint([]);
      return;
    }

    const dateObj = new Date(assess?.measurementDate);

    setCurrPoint([
      {
        x: `${format(dateObj, "LL/dd")} ${format(dateObj, "h:mm a")}`,
        y: value,
        date: format(dateObj, "LL/dd"),
      },
    ]);
  }, [currentAssess, healTraj]);

  useEffect(() => {
    const score = userDetailsAlt?.customerSettings?.woundAssessmentScore;
    if (score) {
      setScoreType(score);
    }
  }, [userDetailsAlt]);

  const woundHealingPictures =
    healTraj && healTraj.length > 0 && healTraj.map((data, index) => <img key={index} src={data.woundPicture} alt="dd" className="d-none" />); //NOSONAR

  return (
    <>
      {isLoading ? <Spinner /> : (
        <>
          <div>
            {woundHealingPictures}
            {healTraj && healTraj.length === 0 ? (
              <div className="p-3">
                <h5>No Data Available.</h5>
              </div>
            ) : (
              <div className="row no-gutters">
                <div className="col-4">
                  <div style={{ position: "relative" }} className={styles.image_block}>
                    <img src={healTraj[currentAssess]?.woundPicture} alt="" className="img-fluid" loading="lazy" />
                    {healTraj && healTraj.length !== 1 && (
                      <div className={styles.rc_outer}>
                        <Slider min={0} max={max} onChange={handleSlider} value={currentAssess} className={styles.rc_style_fix} />
                      </div>
                    )}
                  </div>
                </div>
                <div className={`col ${styles.mesurement_blk}`}>
                  <div className="col-12 mb-1">
                    <div className="row no-gutters">
                      <div className="col-4 p-0">
                        <label className={`d-block mb-2 ${styles.date_heading}`}>
                          {healTraj[currentAssess]?.photoTakenon && format(new Date(healTraj[currentAssess]?.photoTakenon), "MMMM dd, yyyy hh:mm aa")}
                        </label>
                        <span className={styles.surfaceText}>
                          {healTraj[currentAssess]?.dimensions?.surfaceArea?.value !== "Unknown" ? (
                            <>
                              <span>{healTraj[currentAssess]?.dimensions?.surfaceArea?.value}</span>{" "}
                              <span>
                                cm
                                {detailAssessment?.measurement?.isStickerAvailable === 1 && detailAssessment?.isWoundWithoutPhoto === 0 && (
                                  <sup>2</sup>
                                )}
                              </span>
                            </>
                          ) : (
                            "Unknown"
                          )}
                        </span>

                        <div className="mt-2">
                          <label>Status:</label>
                          <span>{woundProgressOptions?.find((opt) => opt.key === healTraj[currentAssess]?.woundStage)?.value || "Unknown"}</span>
                        </div>
                      </div>
                      <div className="col-8 p-0 d-flex justify-content-end">
                        <div className={styles.traj_bwat}>
                          <span className="d-block">{healTraj[currentAssess]?.score ? healTraj[currentAssess]?.score : 0}</span>
                          <label>{scoreType && scoreType === BWAT ? "BWAT" : scoreType && scoreType === "push" ? "PUSH" : "-"}</label>
                        </div>

                        <div className={`${styles.traj_bwat_clouser}`}>
                          <div className={`text-center`}>
                            % Change <br />
                            {healTraj[currentAssess]?.changeInCm ? (
                              healTraj[currentAssess]?.changeInCm !== "Unknown" ? (
                                <span
                                  className={
                                    healTraj[currentAssess]?.changeInCm !== "0"
                                      ? healTraj[currentAssess]?.changeInCm < "0"
                                        ? `${styles.closureColor} ${styles.closureColor_green} mt-2`
                                        : `${styles.closureColor} ${styles.closureColor_red} mt-2`
                                      : `${styles.closureColor} ${styles.closureColor_grey} mt-2`
                                  }
                                >
                                  <span style={{ paddingRight: "4px" }}>
                                    <i
                                      className={
                                        healTraj[currentAssess]?.changeInCm !== "0"
                                          ? healTraj[currentAssess]?.changeInCm < "0"
                                            ? `fa-solid fa-down-long ${styles.closureColor_green}`
                                            : `fa-solid fa-up-long ${styles.closureColor_red}`
                                          : ""
                                      }
                                    ></i>
                                  </span>
                                  {Number(healTraj[currentAssess]?.changeInCm.replace(/^\D+/g, ""))} cm
                                  {detailAssessment?.measurement?.isStickerAvailable === 1 && detailAssessment?.isWoundWithoutPhoto === 0 && (
                                    <sup>2</sup>
                                  )}
                                </span>
                              ) : (
                                <span className={`${styles.closureColor} ${styles.closureColor_grey} mt-2`}>
                                  0 cm
                                  {detailAssessment?.measurement?.isStickerAvailable === 1 && detailAssessment?.isWoundWithoutPhoto === 0 && (
                                    <sup>2</sup>
                                  )}
                                </span>
                              )
                            ) : (
                              "-"
                            )}
                            {healTraj[currentAssess]?.closurePercentage ? (
                              healTraj[currentAssess]?.closurePercentage !== "Unknown" ? (
                                <span
                                  className={
                                    healTraj[currentAssess]?.closurePercentage !== "0"
                                      ? healTraj[currentAssess]?.closurePercentage < "0"
                                        ? `${styles.closureColor} ${styles.closureColor_green} ${healTraj[currentAssess]?.changeInCm !== "0" &&
                                            `pl-2`}`
                                        : `${styles.closureColor} ${styles.closureColor_red} ${healTraj[currentAssess]?.changeInCm !== "0" && `pl-2`}`
                                      : `${styles.closureColor} ${styles.closureColor_grey} ${healTraj[currentAssess]?.changeInCm !== "0" && `pl-2`}`
                                  }
                                >
                                  {`(`}
                                  {healTraj[currentAssess]?.closurePercentage.replace(/^\D+/g, "")} %{`)`}
                                </span>
                              ) : (
                                <span className={`${styles.closureColor} ${styles.closureColor_grey}`}>Unknown</span>
                              )
                            ) : (
                              "-"
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="col-12 mt-4 mb-4">
                    <div className="row no-gutters">
                      {surfaceArray && surfaceArray.length > 0 ? (
                        <HealingTrajectoryChart data={surfaceArray} currPoint={currPoint} />
                      ) : (
                        "No Chart Available."
                      )}
                    </div>
                  </div>

                  <div className="col-12 mb-4">
                    <div className="row no-gutters">
                      <div className="col-6">
                        <p className={styles.traj_heading}>Dimensions</p>
                        <div className="row no-gutters">
                          <div className={`col-12 pl-0  ${styles.measure_surface}`}>
                            <label>Surface Area: </label>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.surfaceArea?.value) ? (
                                <>
                                  <span>{healTraj[currentAssess]?.dimensions?.surfaceArea?.value}</span>
                                  <span>
                                    {" "}
                                    cm
                                    {detailAssessment?.measurement?.isStickerAvailable === 1 && detailAssessment?.isWoundWithoutPhoto === 0 && (
                                      <sup>2</sup>
                                    )}
                                  </span>
                                </>
                              ) : (
                                "Unknown"
                              )}
                            </span>
                          </div>
                          <div className={`col-12 pl-0  ${styles.measure_length}`}>
                            <label>Length: </label>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.length?.value)
                                ? healTraj[currentAssess]?.dimensions?.length?.value + " cm"
                                : "Unknown"}
                            </span>
                          </div>
                          <div className={`col-12 pl-0  ${styles.measure_width}`}>
                            <label>Width: </label>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.width?.value)
                                ? healTraj[currentAssess]?.dimensions?.width?.value + " cm"
                                : "Unknown"}
                            </span>
                          </div>
                          <div className={`col-12 pl-0  ${styles.measure_items}`}>
                            <label>Depth: </label>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.depth?.value)
                                ? healTraj[currentAssess]?.dimensions?.depth?.value + " cm"
                                : "Unknown"}
                            </span>
                          </div>
                          <div className={`col-12 pl-0  ${styles.measure_items}`}>
                            <label>Tunneling: </label>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.tunneling?.value)
                                ? healTraj[currentAssess]?.dimensions?.tunneling?.value + " cm"
                                : "Unknown"}
                            </span>
                          </div>
                          <div className={`col-12 pl-0   ${styles.measure_items}`}>
                            <label>Undermining: </label>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.undermining?.value)
                                ? healTraj[currentAssess]?.dimensions?.undermining?.value + " cm"
                                : "Unknown"}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-6">
                        <p className={styles.traj_heading}>% Change</p>
                        <div className={`row no-gutters`}>
                          <div className={`col-12 pl-0  ${styles.change_surface}`}>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.surfaceArea?.differencePercentage) ? (
                                <span
                                  className={
                                    healTraj[currentAssess]?.dimensions?.surfaceArea?.differencePercentage !== "0"
                                      ? healTraj[currentAssess]?.dimensions?.surfaceArea?.differencePercentage < "0"
                                        ? `${styles.closureColor} ${styles.closureColor_green}`
                                        : `${styles.closureColor} ${styles.closureColor_red}`
                                      : `${styles.closureColor} ${styles.closureColor_grey}`
                                  }
                                >
                                  <span style={{ paddingRight: "4px" }}>
                                    <i
                                      className={
                                        healTraj[currentAssess]?.dimensions?.surfaceArea?.differencePercentage !== "0"
                                          ? healTraj[currentAssess]?.dimensions?.surfaceArea?.differencePercentage < "0"
                                            ? `fa-solid fa-down-long ${styles.closureColor_green}`
                                            : `fa-solid fa-up-long ${styles.closureColor_red}`
                                          : ""
                                      }
                                    ></i>
                                  </span>
                                  {Number(healTraj[currentAssess]?.dimensions?.surfaceArea?.differencePercentage.replace(/^\D+/g, ""))} %
                                </span>
                              ) : (
                                <span className={`${styles.closureColor}`}>Unknown</span>
                              )}
                            </span>
                          </div>
                          <div className={`col-12 pl-0  ${styles.change_length}`}>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.length?.differencePercentage) ? (
                                <span
                                  className={
                                    healTraj[currentAssess]?.dimensions?.length?.differencePercentage !== "0"
                                      ? healTraj[currentAssess]?.dimensions?.length?.differencePercentage < "0"
                                        ? `${styles.closureColor} ${styles.closureColor_green}`
                                        : `${styles.closureColor} ${styles.closureColor_red}`
                                      : `${styles.closureColor} ${styles.closureColor_grey}`
                                  }
                                >
                                  <span style={{ paddingRight: "4px" }}>
                                    <i
                                      className={
                                        healTraj[currentAssess]?.dimensions?.length?.differencePercentage !== "0"
                                          ? healTraj[currentAssess]?.dimensions?.length?.differencePercentage < "0"
                                            ? `fa-solid fa-down-long ${styles.closureColor_green}`
                                            : `fa-solid fa-up-long ${styles.closureColor_red}`
                                          : ""
                                      }
                                    ></i>
                                  </span>
                                  {Number(healTraj[currentAssess]?.dimensions?.length?.differencePercentage.replace(/^\D+/g, ""))} %
                                </span>
                              ) : (
                                <span className={`${styles.closureColor}`}>Unknown</span>
                              )}
                            </span>
                          </div>
                          <div className={`col-12 pl-0   ${styles.change_width}`}>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.width?.differencePercentage) ? (
                                <span
                                  className={
                                    healTraj[currentAssess]?.dimensions?.width?.differencePercentage !== "0"
                                      ? healTraj[currentAssess]?.dimensions?.width?.differencePercentage < "0"
                                        ? `${styles.closureColor} ${styles.closureColor_green}`
                                        : `${styles.closureColor} ${styles.closureColor_red}`
                                      : `${styles.closureColor} ${styles.closureColor_grey}`
                                  }
                                >
                                  <span style={{ paddingRight: "4px" }}>
                                    <i
                                      className={
                                        healTraj[currentAssess]?.dimensions?.width?.differencePercentage !== "0"
                                          ? healTraj[currentAssess]?.dimensions?.width?.differencePercentage < "0"
                                            ? `fa-solid fa-down-long ${styles.closureColor_green}`
                                            : `fa-solid fa-up-long ${styles.closureColor_red}`
                                          : ""
                                      }
                                    ></i>
                                  </span>
                                  {Number(healTraj[currentAssess]?.dimensions?.width?.differencePercentage.replace(/^\D+/g, ""))} %
                                </span>
                              ) : (
                                <span className={`${styles.closureColor}`}>Unknown</span>
                              )}
                            </span>
                          </div>
                          <div className={`col-12 pl-0 ${styles.change_items}`}>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.depth?.differencePercentage) ? (
                                <span
                                  className={
                                    healTraj[currentAssess]?.dimensions?.depth?.differencePercentage !== "0"
                                      ? healTraj[currentAssess]?.dimensions?.depth?.differencePercentage < "0"
                                        ? `${styles.closureColor} ${styles.closureColor_green}`
                                        : `${styles.closureColor} ${styles.closureColor_red}`
                                      : `${styles.closureColor} ${styles.closureColor_grey}`
                                  }
                                >
                                  <span style={{ paddingRight: "4px" }}>
                                    <i
                                      className={
                                        healTraj[currentAssess]?.dimensions?.depth?.differencePercentage !== "0"
                                          ? healTraj[currentAssess]?.dimensions?.depth?.differencePercentage < "0"
                                            ? `fa-solid fa-down-long ${styles.closureColor_green}`
                                            : `fa-solid fa-up-long ${styles.closureColor_red}`
                                          : ""
                                      }
                                    ></i>
                                  </span>
                                  {Number(healTraj[currentAssess]?.dimensions?.depth?.differencePercentage.replace(/^\D+/g, ""))} %
                                </span>
                              ) : (
                                <span className={`${styles.closureColor}`}>Unknown</span>
                              )}
                            </span>
                          </div>
                          <div className={`col-12 pl-0  ${styles.change_items}`}>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.tunneling?.differencePercentage) ? (
                                <span
                                  className={
                                    healTraj[currentAssess]?.dimensions?.tunneling?.differencePercentage !== "0"
                                      ? healTraj[currentAssess]?.dimensions?.tunneling?.differencePercentage < "0"
                                        ? `${styles.closureColor} ${styles.closureColor_green}`
                                        : `${styles.closureColor} ${styles.closureColor_red}`
                                      : `${styles.closureColor} ${styles.closureColor_grey}`
                                  }
                                >
                                  <span style={{ paddingRight: "4px" }}>
                                    <i
                                      className={
                                        healTraj[currentAssess]?.dimensions?.tunneling?.differencePercentage !== "0"
                                          ? healTraj[currentAssess]?.dimensions?.tunneling?.differencePercentage < "0"
                                            ? `fa-solid fa-down-long ${styles.closureColor_green}`
                                            : `fa-solid fa-up-long ${styles.closureColor_red}`
                                          : ""
                                      }
                                    ></i>
                                  </span>
                                  {Number(healTraj[currentAssess]?.dimensions?.tunneling?.differencePercentage.replace(/^\D+/g, ""))} %
                                </span>
                              ) : (
                                <span className={`${styles.closureColor}`}>Unknown</span>
                              )}
                            </span>
                          </div>
                          <div className={`col-12 pl-0  ${styles.change_items}`}>
                            <span className={` ${styles.ch_bold}`}>
                              {allNullCheck(healTraj[currentAssess]?.dimensions?.undermining?.differencePercentage) ? (
                                <span
                                  className={
                                    healTraj[currentAssess]?.dimensions?.undermining?.differencePercentage !== "0"
                                      ? healTraj[currentAssess]?.dimensions?.undermining?.differencePercentage < "0"
                                        ? `${styles.closureColor} ${styles.closureColor_green}`
                                        : `${styles.closureColor} ${styles.closureColor_red}`
                                      : `${styles.closureColor} ${styles.closureColor_grey}`
                                  }
                                >
                                  <span style={{ paddingRight: "4px" }}>
                                    <i
                                      className={
                                        healTraj[currentAssess]?.dimensions?.undermining?.differencePercentage !== "0"
                                          ? healTraj[currentAssess]?.dimensions?.undermining?.differencePercentage < "0"
                                            ? `fa-solid fa-down-long ${styles.closureColor_green}`
                                            : `fa-solid fa-up-long ${styles.closureColor_red}`
                                          : ""
                                      }
                                    ></i>
                                  </span>
                                  {Number(healTraj[currentAssess]?.dimensions?.undermining?.differencePercentage.replace(/^\D+/g, ""))} %
                                </span>
                              ) : (
                                <span className={`${styles.closureColor}`}>Unknown</span>
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="col-12 mb-4">
                    <div className="row no-gutters">
                      <div className="col-6">
                        <div className="col-12 pl-0">
                          <p className={styles.traj_heading}>Healing Progression Rate (HPR)</p>
                        </div>
                        <div className="col-12 pl-0 mb-2 mt-2">
                          <label>Worst Tissue Type (WTT): </label>
                          <span className={` ${styles.ch_bold}`}>
                            {allNullCheck(healTraj[currentAssess]?.healingProgressionRate?.worstTissueType?.value)
                              ? healTraj[currentAssess]?.healingProgressionRate?.worstTissueType?.value !== "N/A"
                                ? healTraj[currentAssess]?.healingProgressionRate?.worstTissueType?.value
                                : "N/A"
                              : "Unknown"}
                          </span>
                        </div>
                        <div className="col-12 pl-0 mb-2 mt-2">
                          <label>Surface Area Value (SAV): </label>
                          <span className={` ${styles.ch_bold}`}>
                            {allNullCheck(healTraj[currentAssess]?.healingProgressionRate?.surfaceAreaValue?.value)
                              ? healTraj[currentAssess]?.healingProgressionRate?.surfaceAreaValue?.value
                              : "Unknown"}
                          </span>
                        </div>
                        <div className="col-12 pl-0 mb-2 mt-2">
                          <label>Intact Value (IV): </label>
                          <span className={` ${styles.ch_bold}`}>
                            {allNullCheck(healTraj[currentAssess]?.healingProgressionRate?.intactValue?.value)
                              ? healTraj[currentAssess]?.healingProgressionRate?.intactValue?.value
                              : "Unknown"}
                          </span>
                        </div>
                      </div>
                      <div className="col-6">
                        <div className="col-12 pl-0">
                          <p className={styles.traj_heading}>% Change</p>
                        </div>
                        <div className="col-12 pl-0 mb-2 mt-2">
                          <span className={` ${styles.ch_bold}`}>
                            {allNullCheck(healTraj[currentAssess]?.healingProgressionRate?.worstTissueType?.differencePercentage) ? (
                              <span
                                className={
                                  healTraj[currentAssess]?.healingProgressionRate?.worstTissueType?.differencePercentage !== "0"
                                    ? healTraj[currentAssess]?.healingProgressionRate?.worstTissueType?.differencePercentage < "0"
                                      ? `${styles.closureColor} ${styles.closureColor_green}`
                                      : `${styles.closureColor} ${styles.closureColor_red}`
                                    : `${styles.closureColor} ${styles.closureColor_grey}`
                                }
                              >
                                <span style={{ paddingRight: "4px" }}>
                                  <i
                                    className={
                                      healTraj[currentAssess]?.healingProgressionRate?.worstTissueType?.differencePercentage !== "0"
                                        ? healTraj[currentAssess]?.healingProgressionRate?.worstTissueType?.differencePercentage < "0"
                                          ? `fa-solid fa-down-long ${styles.closureColor_green}`
                                          : `fa-solid fa-up-long ${styles.closureColor_red}`
                                        : ""
                                    }
                                  ></i>
                                </span>
                                {Number(healTraj[currentAssess]?.healingProgressionRate?.worstTissueType?.differencePercentage.replace(/^\D+/g, ""))}{" "}
                                %
                              </span>
                            ) : (
                              <span className={`${styles.closureColor}`}>Unknown</span>
                            )}
                          </span>
                        </div>
                        <div className="col-12 pl-0 mb-2 mt-2">
                          <span className={` ${styles.ch_bold}`}>
                            {allNullCheck(healTraj[currentAssess]?.healingProgressionRate?.surfaceAreaValue?.differencePercentage) ? (
                              <span
                                className={
                                  healTraj[currentAssess]?.healingProgressionRate?.surfaceAreaValue?.differencePercentage !== "0"
                                    ? healTraj[currentAssess]?.healingProgressionRate?.surfaceAreaValue?.differencePercentage < "0"
                                      ? `${styles.closureColor} ${styles.closureColor_green}`
                                      : `${styles.closureColor} ${styles.closureColor_red}`
                                    : `${styles.closureColor} ${styles.closureColor_grey}`
                                }
                              >
                                <span style={{ paddingRight: "4px" }}>
                                  <i
                                    className={
                                      healTraj[currentAssess]?.healingProgressionRate?.surfaceAreaValue?.differencePercentage !== "0"
                                        ? healTraj[currentAssess]?.healingProgressionRate?.surfaceAreaValue?.differencePercentage < "0"
                                          ? `fa-solid fa-down-long ${styles.closureColor_green}`
                                          : `fa-solid fa-up-long ${styles.closureColor_red}`
                                        : ""
                                    }
                                  ></i>
                                </span>
                                {Number(healTraj[currentAssess]?.healingProgressionRate?.surfaceAreaValue?.differencePercentage.replace(/^\D+/g, ""))}{" "}
                                %
                              </span>
                            ) : (
                              <span className={`${styles.closureColor}`}>Unknown</span>
                            )}
                          </span>
                        </div>
                        <div className="col-12 pl-0 mb-2 mt-2">
                          <span className={` ${styles.ch_bold}`}>
                            {allNullCheck(healTraj[currentAssess]?.healingProgressionRate?.intactValue?.differencePercentage) ? (
                              <span
                                className={
                                  healTraj[currentAssess]?.healingProgressionRate?.intactValue?.differencePercentage !== "0"
                                    ? healTraj[currentAssess]?.healingProgressionRate?.intactValue?.differencePercentage < "0"
                                      ? `${styles.closureColor} ${styles.closureColor_green}`
                                      : `${styles.closureColor} ${styles.closureColor_red}`
                                    : `${styles.closureColor} ${styles.closureColor_grey}`
                                }
                              >
                                <span style={{ paddingRight: "4px" }}>
                                  <i
                                    className={
                                      healTraj[currentAssess]?.healingProgressionRate?.intactValue?.differencePercentage !== "0"
                                        ? healTraj[currentAssess]?.healingProgressionRate?.intactValue?.differencePercentage < "0"
                                          ? `fa-solid fa-down-long ${styles.closureColor_green}`
                                          : `fa-solid fa-up-long ${styles.closureColor_red}`
                                        : ""
                                    }
                                  ></i>
                                </span>
                                {Number(healTraj[currentAssess]?.healingProgressionRate?.intactValue?.differencePercentage.replace(/^\D+/g, ""))} %
                              </span>
                            ) : (
                              <span className={`${styles.closureColor}`}>Unknown</span>
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </>
  );
}
